/**
 * Passwords — Shared infra service pro password operace
 *
 * Module page pro `passwords` modul. Volá `passwords.check_leak` MCP tool
 * který interně dotazuje HaveIBeenPwned přes k-anonymity (heslo neopustí
 * ACM infrastrukturu, jen 5-znakový SHA-1 prefix).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  PageHeader,
} from '../../components/ui';
import { api } from '../../lib/api';
import {
  KeyRound,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
} from 'lucide-react';

type Severity = 'safe' | 'low' | 'medium' | 'high' | 'critical';

interface LeakResult {
  ok: boolean;
  leaked: boolean;
  occurrences: number;
  severity: Severity;
  sha1_prefix: string;
  recommendation: string;
}

const SEVERITY_META: Record<Severity, { labelKey: string; color: string; icon: typeof Shield }> = {
  safe: { labelKey: 'passwords.severitySafe', color: 'text-green-600 dark:text-green-400', icon: ShieldCheck },
  low: { labelKey: 'passwords.severityLow', color: 'text-yellow-600 dark:text-yellow-400', icon: Shield },
  medium: { labelKey: 'passwords.severityMedium', color: 'text-orange-600 dark:text-orange-400', icon: ShieldAlert },
  high: { labelKey: 'passwords.severityHigh', color: 'text-red-600 dark:text-red-400', icon: ShieldAlert },
  critical: { labelKey: 'passwords.severityCritical', color: 'text-red-700 dark:text-red-500', icon: AlertTriangle },
};

export default function PasswordsPage() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [result, setResult] = useState<LeakResult | null>(null);

  const checkMut = useMutation({
    mutationFn: (pwd: string) =>
      api.mcp<LeakResult>('passwords.check_leak', { password: pwd }),
    onSuccess: (data) => {
      setResult(data);
      if (data.leaked) {
        toast.error(t('passwords.leakedToast', { times: data.occurrences.toLocaleString('cs-CZ') }));
      } else {
        toast.success(t('passwords.passwordSafeToast'));
      }
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setResult(null);
    },
  });

  const handleCheck = () => {
    if (!password) {
      toast.error(t('passwords.enterPassword'));
      return;
    }
    checkMut.mutate(password);
  };

  const meta = result ? SEVERITY_META[result.severity] : null;
  const Icon = meta?.icon ?? Shield;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Passwords"
        description={t('passwords.pageDescription')}
      />

      {/* Privacy note */}
      <Card>
        <CardContent className="p-4 flex gap-3 text-sm">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">{t('passwords.privacyTitle')}</p>
            <p className="text-muted-foreground">
              {t('passwords.privacyBody1')}{' '}
              <span className="font-mono">{t('passwords.privacyMono')}</span>{' '}
              {t('passwords.privacyBody2')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Check form */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label>{t('passwords.passwordToCheckLabel')}</Label>
            <div className="flex gap-2 mt-1">
              <div className="flex-1 relative">
                <KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setResult(null);
                  }}
                  placeholder={t('passwords.passwordPlaceholder')}
                  className="pl-9 pr-10 font-mono"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCheck();
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  title={show ? t('passwords.hide') : t('passwords.show')}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button onClick={handleCheck} disabled={checkMut.isPending || !password}>
                {checkMut.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4 mr-1" />
                )}
                {t('passwords.checkButton')}
              </Button>
            </div>
          </div>

          {/* Result */}
          {result && meta && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <Icon className={`w-8 h-8 flex-shrink-0 ${meta.color}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`text-lg font-semibold ${meta.color}`}>{t(meta.labelKey)}</p>
                    {result.leaked && (
                      <Badge variant="error">
                        {t('passwords.leakedBadge', { times: result.occurrences.toLocaleString('cs-CZ') })}
                      </Badge>
                    )}
                    {!result.leaked && <Badge variant="default">{t('passwords.notLeakedBadge')}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {result.recommendation}
                  </p>
                </div>
              </div>

              <details className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                <summary className="cursor-pointer select-none">{t('passwords.technicalDetail')}</summary>
                <div className="mt-2 space-y-1 font-mono">
                  <div>
                    {t('passwords.sha1PrefixLabel')}{' '}
                    <span className="text-foreground">{result.sha1_prefix}</span>
                  </div>
                  <div>
                    Severity: <span className="text-foreground">{result.severity}</span>
                  </div>
                  <div>
                    Occurrences:{' '}
                    <span className="text-foreground">{result.occurrences}</span>
                  </div>
                </div>
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Programmatic usage */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            {t('passwords.callableTitle')}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('passwords.callableBody')}{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">callModuleTool</code>:
          </p>
          <pre className="text-xs bg-muted/50 rounded p-3 overflow-x-auto font-mono">
{`const leak = await ctx.callModuleTool('passwords', 'check_leak', {
  password: generatedPassword,
});
if (leak.leaked && leak.severity === 'critical') {
  // regenerate password & retry
}`}
          </pre>
          <p className="text-sm text-muted-foreground">
            {t('passwords.externalClientBody')}{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
              POST /mcp · tools/call · name: passwords.check_leak
            </code>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
